/**
 * Het plusmenu bij het chatveld. I7, 17 oktober 2026.
 *
 * Opdracht Frank: op BEIDE plekken van de plus (welkomstscherm en gesprek)
 * hetzelfde menu, met Bestanden (de bestaande bijlage), Connectors met een
 * aan/uit-schakelaar per connector per gesprek en een knop naar Instellingen
 * (sinds 23 oktober: "Koppelingen beheren", het Koppelingen-venster),
 * Skills, en Plugins als plek voor later. `SwitchMenu` is het voorbeeld: dezelfde
 * Radix-primitief, hetzelfde vakje, en het menu klapt niet dicht bij een klik op
 * een schakelaar.
 *
 * I8: ook de bestaande koppelingen (Google, Nextcloud, Fathom, TASQ) staan hier,
 * met dezelfde schakelaar; uit betekent dat de orchestrator ze in dit gesprek
 * niet leest, ook als het woord in de vraag staat.
 *
 * Een kijker ziet de connectors met hun stand, maar geen schakelaars (de backend
 * weigert hem ook, 403).
 *
 * MK6 (GO Frank 3 november 2026): de volgorde van claude.ai. Bestanden, Vaardigheden, Koppelingen (met een
 * waarschuwingsteken bij een storing), Onderzoek (web in dit gesprek), Geheugen (in dit gesprek, standaard aan; besluit
 * Frank), Plugins (later). Onderzoek kan hier alleen UIT voor dit gesprek: staat het web in Instellingen dicht, dan
 * blijft het dicht en staat dat erbij, want een gesprek mag de schakelaar van het schakelbord niet openzetten.
 */

import { useState } from "react";
import * as Menu from "@radix-ui/react-dropdown-menu";
import { AlertTriangle, Brain, Globe, Paperclip, Plug, Plus, Puzzle, Settings2, Sparkles } from "lucide-react";

import { Button } from "./ui/button";
import { Vakje } from "./SwitchMenu";
import { magKoppelen } from "./NextcloudConnection";
import { cn } from "../lib/utils";
import KoppelingenVenster from "./KoppelingenVenster";
import { useTaal } from "@/contexts/TaalContext";

const REGEL = "relative flex cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50";

/** Wat er achter een connector staat als hij niet aan kan. PUUR. */
export function standVan(c, t) {
  if (!c.server_aan) return t("uit in Instellingen");
  return c.aan_hier ? t("aan") : t("uit in dit gesprek");
}

/** MK6: het waarschuwingsteken bij een koppeling met een storing, met de zin van de laatste toets als uitleg. */
function Storing({ c, t }) {
  if (!c.fout) return null;
  return (
    <span className="shrink-0 text-[hsl(var(--accent))]" title={c.fout} aria-label={t("Storing: {fout}", { fout: c.fout })}
          data-testid={`plus-storing-${c.id}`}>
      <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
    </span>
  );
}

/** MK6: de volgorde van de delen, zoals claude.ai. PUUR, en de toets leest hem uit het scherm. */
export const DELEN = ["bestanden", "vaardigheden", "koppelingen", "onderzoek", "geheugen", "plugins"];

export default function PlusMenu({
  onFiles, filesDisabled = false, connectors = [], rol = "", onToggleConnector,
  onOpenSkills, className = "", opties = null, onZetOptie, webInInstellingen = false,
}) {
  const stand = { geheugen: true, onderzoek: true, ...(opties || {}) };
  const { t } = useTaal();
  const mag = magKoppelen(rol);
  // BESLUIT FRANK 23 okt (deel 3): "Koppelingen beheren" opent het bestaande
  // Koppelingen-venster, dat niet meer in het linker menu staat.
  const [koppelingenOpen, setKoppelingenOpen] = useState(false);

  return (
    <>
    <Menu.Root>
      <Menu.Trigger asChild>
        <Button type="button" variant="ghost" size="icon" aria-label={t("Meer")}
                data-testid="plus-menu-trigger"
                className={cn("tap-target rounded-xl flex-shrink-0", className)}>
          <Plus className="h-4 w-4" />
        </Button>
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="start" sideOffset={6} data-testid="plus-menu"
          className="z-50 max-h-[60vh] w-72 overflow-y-auto overflow-x-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md">
          <Menu.Item className={REGEL} disabled={filesDisabled} onSelect={() => onFiles && onFiles()}
                     data-testid="plus-bestanden" data-deel="bestanden">
            <Paperclip className="h-4 w-4" aria-hidden="true" />{t("Bestanden")}
          </Menu.Item>
          <Menu.Item className={REGEL} onSelect={() => onOpenSkills && onOpenSkills()} data-testid="plus-skills"
                     data-deel="vaardigheden">
            <Sparkles className="h-4 w-4" aria-hidden="true" />{t("Vaardigheden")}
          </Menu.Item>

          <Menu.Separator className="my-1 h-px bg-border" />
          <Menu.Label className="px-2 py-1.5 text-xs text-muted-foreground" data-deel="koppelingen">
            <Plug className="h-3 w-3 inline mr-1" aria-hidden="true" />{t("Koppelingen in dit gesprek")}
          </Menu.Label>
          {connectors.length === 0 && (
            <p className="px-2 py-1 text-xs text-muted-foreground" data-testid="plus-geen-connectors">
              {t("Nog geen koppelingen.")}
            </p>
          )}
          {connectors.map(c => (mag && c.server_aan ? (
            <Menu.CheckboxItem key={c.id} checked={c.aan_hier} className={REGEL}
              onSelect={e => e.preventDefault()}
              onCheckedChange={() => onToggleConnector && onToggleConnector(c.id, !c.aan_hier, c.soort || "mcp")}
              data-testid={`plus-connector-${c.id}`}>
              <Vakje stand={c.aan_hier ? "aan" : "uit"} />
              <span className="flex-1 truncate">{c.naam}</span>
              <Storing c={c} t={t} />
            </Menu.CheckboxItem>
          ) : (
            <div key={c.id} className="flex items-center gap-2 px-2 py-1.5 text-sm"
                 data-testid={`plus-connector-stand-${c.id}`}>
              <span className="flex-1 truncate">{c.naam}</span>
              <Storing c={c} t={t} />
              <span className="shrink-0 text-xs opacity-70">{standVan(c, t)}</span>
            </div>
          )))}
          <Menu.Item className={REGEL} onSelect={() => setKoppelingenOpen(true)}
                     data-testid="plus-beheren">
            <Settings2 className="h-4 w-4" aria-hidden="true" />{t("Koppelingen beheren")}
          </Menu.Item>

          <Menu.Separator className="my-1 h-px bg-border" />
          {/* MK6: Onderzoek, het web in DIT gesprek. Alleen uit te zetten; dicht in Instellingen blijft dicht. */}
          {webInInstellingen && mag ? (
            <Menu.CheckboxItem checked={stand.onderzoek} className={REGEL} onSelect={e => e.preventDefault()}
              onCheckedChange={() => onZetOptie && onZetOptie("onderzoek", !stand.onderzoek)}
              data-testid="plus-onderzoek" data-deel="onderzoek">
              <Globe className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{t("Onderzoek")}</span>
              <Vakje stand={stand.onderzoek ? "aan" : "uit"} />
            </Menu.CheckboxItem>
          ) : (
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm" data-testid="plus-onderzoek-stand" data-deel="onderzoek">
              <Globe className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{t("Onderzoek")}</span>
              <span className="shrink-0 text-xs opacity-70">{t(webInInstellingen ? (stand.onderzoek ? "aan" : "uit in dit gesprek") : "uit in Instellingen")}</span>
            </div>
          )}
          {/* MK6: Geheugen in dit gesprek, standaard aan (besluit Frank 3 nov). Een kijker ziet de stand. */}
          {mag ? (
            <Menu.CheckboxItem checked={stand.geheugen} className={REGEL} onSelect={e => e.preventDefault()}
              onCheckedChange={() => onZetOptie && onZetOptie("geheugen", !stand.geheugen)}
              data-testid="plus-geheugen" data-deel="geheugen">
              <Brain className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{t("Geheugen")}</span>
              <Vakje stand={stand.geheugen ? "aan" : "uit"} />
            </Menu.CheckboxItem>
          ) : (
            <div className="flex items-center gap-2 px-2 py-1.5 text-sm" data-testid="plus-geheugen-stand" data-deel="geheugen">
              <Brain className="h-4 w-4" aria-hidden="true" />
              <span className="flex-1">{t("Geheugen")}</span>
              <span className="shrink-0 text-xs opacity-70">{t(stand.geheugen ? "aan" : "uit in dit gesprek")}</span>
            </div>
          )}

          <Menu.Separator className="my-1 h-px bg-border" />
          <Menu.Item className={REGEL} disabled data-testid="plus-plugins" data-deel="plugins">
            <Puzzle className="h-4 w-4" aria-hidden="true" />{t("Plugins")}
            <span className="ml-auto text-xs opacity-70">{t("komt later")}</span>
          </Menu.Item>
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
    <KoppelingenVenster open={koppelingenOpen} onOpenChange={setKoppelingenOpen} />
    </>
  );
}
